/* Proven in live workflows (7 Sep 2026, GSAP edition).
   - Milestone reels: each digit is a 20-glyph reel (0-9 twice). On entry every reel rolls forward a few turns
     and lands exactly on its published digit, left to right, with an expo settle. No fractional mush.
   - Stat row counts up beside it. Trader count is read from the theme's own [data-trader-count] hook.
   - Illustrative counter: small sample increments every 0.5–1 second while the number is visible.
     This is labelled in the markup and never replaces the published milestone or accessible statement.
   - Illustrative traffic: alert packets travel TradingView -> PineConnector -> MetaTrader in view.
   Without GSAP or under reduced motion the static markup (final figure) is left as-is. */
(() => {
  if (window.PCSignalProof) { window.PCSignalProof.scan(); return; }
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const has = n => typeof window[n] !== 'undefined';
  const fmt = new Intl.NumberFormat('en-US');
  const roots = new Map();

  function init(root) {
    if (roots.has(root)) return;
    roots.set(root, () => {});
    const display = root.querySelector('.pcpf__display');
    const number = root.querySelector('[data-pcpf-counter]');
    const host = root.closest('#PCSignalJourney');
    const reels = [...root.querySelectorAll('.pcpf__reel')];
    const stats = [...root.querySelectorAll('[data-pcpf-count]')];
    const source = root.querySelector('[data-trader-count]');
    const plus = root.querySelector('.pcpf__plus');
    const ring = root.querySelector('.pcpf__ring');
    const pcDot = root.querySelector('.pcpf__dot');
    const nodes = [...root.querySelectorAll('.pcpf__node')];
    const wires = [...root.querySelectorAll('.pcpf__wire')];

    function statValue(el) {
      if (el.dataset.pcpfSource === 'traders' && source) {
        const txt = source.textContent.trim(), num = Number(txt.replace(/[^0-9.]/g, ''));
        if (num > 0) { el.dataset.pcpfCount = String(num); el.dataset.pcpfSuffix = /\+/.test(txt) ? '+' : ''; }
      }
      return { value: Number(el.dataset.pcpfCount) || 0, decimals: Number(el.dataset.pcpfDecimals || 0), suffix: el.dataset.pcpfSuffix || '' };
    }
    const format = (v, dec) => dec ? v.toFixed(dec) : fmt.format(Math.round(v));
    const statsFinal = () => stats.forEach(el => { const s = statValue(el); el.textContent = format(s.value, s.decimals) + s.suffix; });
    const sourceObserver = source ? new MutationObserver(() => { if (root.dataset.pcpfPlayed === 'true') statsFinal(); }) : null;
    sourceObserver?.observe(source, { childList: true, characterData: true, subtree: true });

    const staticMode = reduced.matches || !has('gsap') || !has('ScrollTrigger');
    if (staticMode) { root.classList.add('is-in'); root.dataset.pcpfIn = 'true'; root.dataset.pcpfPlayed = 'true'; statsFinal(); roots.set(root, () => sourceObserver?.disconnect()); return; }

    gsap.registerPlugin(ScrollTrigger);
    root.dataset.pcpfArmed = 'true';
    root.dataset.pcpfIn = 'false';

    let sample = Number(number?.dataset.pcpfCounter) || 193792255;
    let ticker = 0, numberInView = false, disposed = false;
    const canTick = () => !disposed && root.isConnected && numberInView && !document.hidden && !reduced.matches && !host?.classList.contains('is-paused') && root.dataset.pcpfPlayed === 'true';
    function stopTicking() { clearTimeout(ticker); ticker = 0; }
    function scheduleTick() {
      if (ticker || !canTick()) return;
      ticker = setTimeout(() => {
        ticker = 0;
        if (!canTick()) return;
        const next = sample + 2 + Math.floor(Math.random() * 7);
        const oldDigits = String(sample).padStart(reels.length, '0');
        const newDigits = String(next).padStart(reels.length, '0');
        // A digit-width rollover is outside this decorative display's useful lifetime.
        if (newDigits.length !== reels.length) return;
        reels.forEach((reel, i) => {
          const previous = Number(oldDigits[i]), digit = Number(newDigits[i]);
          if (previous === digit) return;
          const steps = (digit - previous + 10) % 10;
          reel.dataset.end = String(10 + digit);
          reel.parentElement.dataset.d = String(digit);
          // Two copies of 0–9 allow forward carries without a reverse jump.
          gsap.fromTo(reel, { '--p': previous }, { '--p': previous + steps, duration: .28, ease: 'power2.inOut', overwrite: true, onComplete: () => gsap.set(reel, { '--p': 10 + digit }) });
        });
        sample = next;
        number.dataset.pcpfCounter = String(sample);
        scheduleTick();
      }, 500 + Math.random() * 500);
    }
    const numberObserver = new IntersectionObserver(([entry]) => {
      numberInView = entry.isIntersecting;
      if (numberInView) scheduleTick(); else stopTicking();
    }, { threshold: .5 });
    if (number) numberObserver.observe(number);

    /* Entrance: reels roll home, stats count up. */
    const entrance = gsap.timeline({
      scrollTrigger: { trigger: display || root, start: 'top 72%', once: true },
      onStart: () => { root.classList.add('is-in'); root.dataset.pcpfRolling = 'true'; },
      onComplete: () => { root.dataset.pcpfRolling = 'false'; root.dataset.pcpfPlayed = 'true'; statsFinal(); scheduleTick(); }
    });
    reels.forEach((reel, i) => {
      const end = Number(reel.dataset.end);            // index in the 20-glyph reel (10..19)
      const spins = 4 + Math.round(i * .7);            // right-hand reels travel further
      entrance.fromTo(reel, { '--p': end - spins }, { '--p': end, duration: 1.7 + i * .1, ease: 'expo.out' }, i * .055);
    });
    stats.forEach((el, i) => {
      const s = statValue(el), o = { v: 0 };
      entrance.to(o, { v: s.value, duration: 1.9, ease: 'expo.out', onUpdate: () => { el.textContent = format(o.v, s.decimals); }, onComplete: () => { el.textContent = format(s.value, s.decimals) + s.suffix; } }, .35 + i * .14);
    });

    /* Live traffic: one packet in flight per wire, arrivals pulse the node and the plus. */
    const ink = plus ? getComputedStyle(root).color : null;
    const blue = plus ? getComputedStyle(plus).color : null;
    function pulse() {
      const t = gsap.timeline();
      if (pcDot) t.fromTo(pcDot, { scale: 1, opacity: 1 }, { scale: 2.6, opacity: 0, duration: .7, ease: 'power2.out', clearProps: 'all' }, 0);
      if (nodes[1]) t.fromTo(nodes[1], { opacity: 1 }, { opacity: .55, duration: .12, yoyo: true, repeat: 1 }, 0);
      if (plus && ink && blue) t.fromTo(plus, { scale: 1, color: blue }, { scale: 1.22, color: ink, duration: .16, ease: 'power2.out', yoyo: true, repeat: 1, clearProps: 'color,scale' }, 0);
      if (ring) t.fromTo(ring, { scale: .4, opacity: .9 }, { scale: 2.1, opacity: 0, duration: .8, ease: 'power2.out', clearProps: 'all' }, 0);
      return t;
    }
    function deliver() {
      const t = gsap.timeline();
      if (nodes[2]) t.fromTo(nodes[2], { opacity: 1 }, { opacity: .5, duration: .12, yoyo: true, repeat: 1 }, 0);
      return t;
    }
    let traffic = null;
    function run() {
      if (wires.length < 2) return;
      const [w1, w2] = wires, p1 = w1.querySelector('i'), p2 = w2.querySelector('i');
      const len = p => p.offsetWidth || 72;                        // comet length
      const span = w => Math.max(0, w.clientWidth - 72);           // travel so the comet head stops at the wire end
      const tl = gsap.timeline({ onComplete: () => { traffic = gsap.delayedCall(gsap.utils.random(.15, .75), run); } });
      tl.fromTo(p1, { left: 0, x: -len(p1), opacity: 0 }, { x: span(w1), opacity: 1, duration: gsap.utils.random(1.0, 1.35), ease: 'power1.in' })
        .to(p1, { opacity: 0, duration: .12 })
        .add(pulse(), '-=.1')
        .fromTo(p2, { left: 0, x: -len(p2), opacity: 0 }, { x: span(w2), opacity: 1, duration: gsap.utils.random(1.0, 1.35), ease: 'power1.out' }, '-=.5')
        .to(p2, { opacity: 0, duration: .12 })
        .add(deliver(), '-=.1');
      traffic = tl;
    }
    const trafficTrigger = ScrollTrigger.create({
      trigger: root, start: 'top bottom', end: 'bottom top',
      onEnter: () => { root.dataset.pcpfIn = 'true'; if (!traffic) run(); else traffic.resume?.(); },
      onEnterBack: () => { root.dataset.pcpfIn = 'true'; if (!traffic) run(); else traffic.resume?.(); },
      onLeave: () => { root.dataset.pcpfIn = 'false'; traffic?.pause?.(); },
      onLeaveBack: () => { root.dataset.pcpfIn = 'false'; traffic?.pause?.(); }
    });

    function syncMotion() {
      if (reduced.matches) {
        entrance.progress(1);
        gsap.killTweensOf(reels);
        reels.forEach(reel => gsap.set(reel, { '--p': Number(reel.dataset.end) }));
        root.classList.add('is-in'); statsFinal();
      }
      if (canTick()) scheduleTick(); else stopTicking();
      if (document.hidden || reduced.matches || host?.classList.contains('is-paused')) traffic?.pause?.();
      else if (root.dataset.pcpfIn === 'true') traffic?.resume?.();
    }
    const motionObserver = new MutationObserver(syncMotion);
    if (host) motionObserver.observe(host, { attributes: true, attributeFilter: ['class'] });
    document.addEventListener('visibilitychange', syncMotion);
    reduced.addEventListener('change', syncMotion);
    roots.set(root, () => {
      disposed = true; stopTicking(); numberObserver.disconnect(); motionObserver.disconnect(); sourceObserver?.disconnect();
      document.removeEventListener('visibilitychange', syncMotion); reduced.removeEventListener('change', syncMotion);
      entrance.scrollTrigger?.kill(); entrance.kill(); traffic?.kill?.(); trafficTrigger.kill(); gsap.killTweensOf(reels);
    });

    /* Cursor tilt on the quote cards (fine pointers): the card leans toward the pointer and springs back. */
    if (matchMedia('(pointer: fine)').matches) root.querySelectorAll('.pcpf__card').forEach(card => {
      card.classList.add('is-tilt');
      card.addEventListener('pointermove', e => {
        const r = card.getBoundingClientRect(), px = (e.clientX - r.left) / r.width - .5, py = (e.clientY - r.top) / r.height - .5;
        gsap.to(card, { rotateY: px * 10, rotateX: -py * 8, y: -4, transformPerspective: 900, duration: .4, ease: 'power3.out', overwrite: 'auto' });
      });
      card.addEventListener('pointerleave', () => gsap.to(card, { rotateY: 0, rotateX: 0, y: 0, duration: .9, ease: 'elastic.out(1, .5)', overwrite: 'auto' }));
    });
  }

  function scan(scope = document) { scope.querySelectorAll('[data-pcpf]').forEach(init); }
  document.addEventListener('shopify:section:load', e => scan(e.target));
  document.addEventListener('shopify:section:unload', e => { for (const [root, dispose] of roots) if (e.target.contains(root)) { dispose(); roots.delete(root); } });
  window.PCSignalProof = { scan };
  scan();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
})();
